import React, { useState } from 'react';
import { HbrOption, HbrSelect } from '@harbor/elements-react-standalone';
import StbButton from '../security-toolbox-ui/StbButton';
import { asSingleValue } from '../../support/utils';
import { useLabSweeps } from '../../hooks/lab-hooks';
import { LabSweep, LabSweepVerdict, labSweepSentiment } from '../../support/labService';
import { LabCard, LabDate, LabJsonView, LabPanelState, LabStatusTag, labDuration } from './LabShared';

const LIMITS = ['5', '20', '50', '100'];

const VERDICT_LABELS: Record<LabSweepVerdict, string> = {
  ok: 'OK',
  problem: 'Problem',
  unreachable: 'Unreachable',
  not_ready: 'Not ready',
  error: 'Error',
  skipped: 'Skipped',
};

const sweepStateSentiment = (state: LabSweep['state']) => {
  if (state === 'completed') return 'success' as const;
  if (state === 'failed') return 'danger' as const;
  if (state === 'partial') return 'warning' as const;
  return 'info' as const;
};

const elapsedSeconds = (sweep: LabSweep): number | null => {
  if (!sweep.started_at || !sweep.finished_at) return null;

  const started = Date.parse(sweep.started_at);
  const finished = Date.parse(sweep.finished_at);

  if (Number.isNaN(started) || Number.isNaN(finished)) return null;

  return Math.round((finished - started) / 1000);
};

/**
 * Fleet sweep history.
 *
 * Read-only here, and deliberately: sweeps are started by `sweep_lab_fleet` on a schedule, not
 * by somebody clicking during an event. What this view is for is the historical question - a
 * pod that comes back `problem` every morning is a template fault, and that only shows up
 * across sweeps rather than in the latest one.
 *
 * `unreachable` and `problem` mean different things and are kept apart. A pod nothing could
 * reach might be fine; a pod that answered and reported a device down is not.
 */
const LabSweepsPanel: React.FC = () => {
  const [limit, setLimit] = useState('20');
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data: sweeps, isLoading, error, refetch } = useLabSweeps(Number(limit));

  return (
    <LabCard
      title="Fleet sweeps"
      subtitle="Scheduled health passes over every live pod. Newest first."
      actions={
        <>
          <div className="min-w-[140px]">
            <HbrSelect
              value={limit}
              size="small"
              onHbr-change={(evt: any) => setLimit(asSingleValue(evt.target.value))}
            >
              {LIMITS.map((option) => (
                <HbrOption value={option} key={option}>
                  {`Last ${option}`}
                </HbrOption>
              ))}
              <div slot="label">Show</div>
            </HbrSelect>
          </div>
          <StbButton role="default" size="small" onClick={async () => refetch()}>
            Refresh
          </StbButton>
        </>
      }
    >
      <LabPanelState
        loading={isLoading}
        error={error}
        empty={!sweeps?.length}
        emptyMessage="No sweeps recorded. They are started by the sweep_lab_fleet job, not from here."
        loadingLabel="Loading sweeps..."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2 font-semibold">Sweep</th>
                <th className="px-3 py-2 font-semibold">State</th>
                <th className="px-3 py-2 font-semibold">Pods</th>
                <th className="px-3 py-2 font-semibold">Started</th>
                <th className="px-3 py-2 font-semibold">Took</th>
                <th className="px-3 py-2 font-semibold text-right">Results</th>
              </tr>
            </thead>

            <tbody>
              {(sweeps || []).map((sweep) => {
                const took = elapsedSeconds(sweep);

                return (
                  <React.Fragment key={sweep.id}>
                    <tr className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{sweep.label || `Sweep ${sweep.id}`}</div>
                        <div className="text-xs text-gray-500">
                          {sweep.initiated_by_email || 'scheduled'}
                        </div>
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabStatusTag
                          label={sweep.state}
                          sentiment={sweepStateSentiment(sweep.state)}
                        />
                        {sweep.error_message && (
                          <div className="text-xs text-red-700">{sweep.error_message}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        <div>{`${sweep.sessions_seen} seen`}</div>
                        <div className="text-green-700">{`${sweep.sessions_ok} ok`}</div>
                        {sweep.sessions_problem > 0 && (
                          <div className="text-amber-700">{`${sweep.sessions_problem} with problems`}</div>
                        )}
                        {sweep.sessions_error > 0 && (
                          <div className="text-red-700">{`${sweep.sessions_error} errored`}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        <LabDate value={sweep.started_at} />
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {took === null ? 'still running' : labDuration(took)}
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end">
                          <StbButton
                            role="default"
                            size="xsmall"
                            onClick={async () =>
                              setExpanded(expanded === sweep.id ? null : sweep.id)
                            }
                          >
                            {expanded === sweep.id
                              ? 'Hide'
                              : `${(sweep.results || []).length} pods`}
                          </StbButton>
                        </div>
                      </td>
                    </tr>

                    {expanded === sweep.id && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td className="px-3 py-3" colSpan={6}>
                          {(sweep.results || []).length ? (
                            <table className="w-full text-left text-xs">
                              <thead>
                                <tr className="border-b border-gray-200 uppercase tracking-wide text-gray-500">
                                  <th className="px-2 py-1 font-semibold">Pod</th>
                                  <th className="px-2 py-1 font-semibold">Verdict</th>
                                  <th className="px-2 py-1 font-semibold">Devices</th>
                                  <th className="px-2 py-1 font-semibold">Agents</th>
                                  <th className="px-2 py-1 font-semibold">Run</th>
                                  <th className="px-2 py-1 font-semibold">Problems</th>
                                </tr>
                              </thead>

                              <tbody>
                                {[...(sweep.results || [])]
                                  .sort((left, right) =>
                                    left.verdict === right.verdict
                                      ? left.session_key.localeCompare(right.session_key)
                                      : left.verdict === 'ok'
                                        ? 1
                                        : -1,
                                  )
                                  .map((result) => (
                                    <tr key={result.id} className="border-b border-gray-100 align-top">
                                      <td className="px-2 py-1">
                                        <div>{result.session_name || result.session_key}</div>
                                        <div className="font-mono text-gray-500">
                                          {result.session_key}
                                        </div>
                                      </td>

                                      <td className="px-2 py-1 whitespace-nowrap">
                                        <LabStatusTag
                                          label={VERDICT_LABELS[result.verdict] || result.verdict}
                                          sentiment={labSweepSentiment(result.verdict)}
                                        />
                                      </td>

                                      <td className="px-2 py-1 whitespace-nowrap">
                                        {`${result.devices_up}/${result.devices_total} up`}
                                        {result.devices_down > 0 && (
                                          <div className="text-red-700">
                                            {`${result.devices_down} down`}
                                          </div>
                                        )}
                                        {result.devices_stale > 0 && (
                                          // A stale reading is not a good reading.
                                          <div className="text-amber-700">
                                            {`${result.devices_stale} stale`}
                                          </div>
                                        )}
                                      </td>

                                      <td className="px-2 py-1 whitespace-nowrap">
                                        {result.agents_online === 0 ? (
                                          <span className="text-red-700">none online</span>
                                        ) : (
                                          `${result.agents_online} online`
                                        )}
                                      </td>

                                      <td className="px-2 py-1 whitespace-nowrap">
                                        {result.run_state || '—'}
                                      </td>

                                      <td className="px-2 py-1">
                                        {(result.problems || []).length ? (
                                          <ul className="m-0 list-disc pl-4">
                                            {(result.problems || []).map((problem) => (
                                              <li key={problem}>{problem}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          '—'
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          ) : (
                            <p className="m-0 text-xs text-gray-600">
                              This sweep recorded no pods. Either nothing was live, or it failed
                              before it got to any.
                            </p>
                          )}

                          {sweep.summary && (
                            <details className="mt-2">
                              <summary className="cursor-pointer text-xs uppercase tracking-wide text-gray-500">
                                Summary
                              </summary>
                              <LabJsonView value={sweep.summary} />
                            </details>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </LabPanelState>
    </LabCard>
  );
};

export default LabSweepsPanel;
